import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { dataAPI } from "../../api/axios";
import { Button } from "@progress/kendo-react-buttons";
import "./Attachments.css";
import { useSelector } from "react-redux";
import AddAttachment from "./AddAttachment";
import DeleteAttachment from "./DeleteAttachment";
import { makeArray } from "../../permissionUtils";
import { getCookie } from "../../api/axios";
import jwt_decode from "jwt-decode";

/**
 * @typedef {Object} Attachment
 * @property {string} contentType
 * @property {number} id
 * @property {string} name
 * @property {number} size
 * @property {number} oid
 */

/**
 * @typedef {Object} Permission
 * @property {string} Type - The type of the permission.
 * @property {string} Value - The name of the role.
 * @property {boolean} Read - Whether the role has read permission.
 * @property {boolean} [Create] - Whether the role has create permission.
 * @property {boolean} [Update] - Whether the role has update permission.
 * @property {boolean} [Delete] - Whether the role has delete permission.
 */

/**
 * @typedef {Object} Permissions
 * @property {Array<Permission> | Permission} Permission - An array of Permission objects.
 */

/**
 * @typedef {Object} AttachmentsConfig
 * @property {number} AttachmentTableID - The ID of the attachment table.
 * @property {Permissions} [Permissions] - The permissions for the attachment table.
 */

/**
 * @typedef {Object} AttachmentspropAttachments
 * @property {Array<Attachment>} attachments
 * @property {string} layerId
 * @property {string} featureId
 * @property {Object} AttachmentsConfig
 *
 */

/**
 *
 * @param {Attachments} propAttachments
 * @returns JSX
 */
const Attachments = ({ layerId, featureId, AttachmentsConfig }) => {
    /**
     *   Set Variables
     */

    const [attachments, setAttachments] = useState([]);

    const [show, setShow] = useState(false);
    const [attachmentsIndex, setAttachmentsIndex] = useState(0);
    const vectorLayer = useSelector((state) =>
        state.configData.vectorLayerProperties.find((l) => l.Id === layerId)
    );

    /** @type {AttachmentsConfig} */
    const attachmentsLayer = vectorLayer?.Attachments?.Attachment;

    const [content, setContent] = useState();
    const [dialogContext, setDialogContext] = useState("viewAttachment");

    /** @type {AttachmentsConfig} */

    /** @type {Array<string>} */
    const tokenRoles = useMemo(() => getTokenRoles(), []);

    const canCreateAttachment = useMemo(
        () => checkPermission(attachmentsLayer, tokenRoles, "Create"),
        [attachmentsLayer, tokenRoles]
    );

    const canDeleteAttachment = useMemo(
        () => checkPermission(attachmentsLayer, tokenRoles, "Delete"),
        [attachmentsLayer, tokenRoles]
    );

    useEffect(() => {
        async function fetchData() {
            let d = await getFeatureAttachments(
                layerId,
                featureId,
                AttachmentsConfig.Attachment.AttachmentTableID
            );
            setAttachments(d);
        }
        fetchData();
    }, [attachmentsLayer, featureId, layerId]);

    useEffect(() => {
        setAttachmentsIndex(0); // Reset attachmentsIndex when propAttachments or newAttachments change
    }, []);

    /**
     * Handle Events
     */
    const onClick = () => {
        setShow(!show);
    };

    const downloadAttachment = (dataURL, name) => {
        const link = document.createElement("a");
        link.href = dataURL;
        link.download = name; // or any other filename you want
        link.click();
    };

    const handleLeftArrowClick = () => {
        if (attachments.length === 0) {
            return; // No attachments to navigate
        }
        const newIndex =
            attachmentsIndex === 0 ? attachments.length - 1 : attachmentsIndex - 1;
        setAttachmentsIndex(newIndex);
    };

    const handleRightArrowClick = () => {
        if (attachments.length === 0) {
            return; // No attachments to navigate
        }
        const newIndex =
            attachmentsIndex >= attachments.length - 1 ? 0 : attachmentsIndex + 1;
        setAttachmentsIndex(newIndex);
    };

    const handleAddAttachment = () => {
        setDialogContext("addAttachment");
    };

    const handleDeleteAttachment = async (attachmentId) => {
        // Perform deletion operation
        setDialogContext("deleteAttachment");
        try {
            // Update attachments state to reflect the changes
            const updatedAttachments = attachments.filter(
                (attachment) => attachment.id !== attachmentId
            );
            if (attachmentsIndex >= updatedAttachments.length) {
                setAttachmentsIndex(0);
            }

            setAttachments(updatedAttachments);
        } catch (error) {
            console.error("Error deleting attachment:", error);
        }

        setDialogContext("viewAttachment");
    };

    /**
     * useEffects
     */
    useEffect(() => {
        const attachment = attachments[attachmentsIndex];
        if (!attachment) {
            return () => {
                setContent(undefined);
            };
        }

        const attachmentId = attachment.id;
        const fileExtension = attachment.name.split(".").pop().toLowerCase();

        const fetchAttachmentContent = async () => {
            try {
                const dataUrl = await getAttachment(
                    layerId,
                    featureId,
                    attachmentsLayer.AttachmentTableID,
                    attachmentId
                );
                if (dataUrl) {
                    setContent({
                        src: dataUrl,
                        type:
                            fileExtension === "jpg" ||
                            fileExtension === "jpeg" ||
                            fileExtension === "png" ||
                            fileExtension === "gif"
                                ? "image"
                                : "link",
                    });
                }
            } catch (error) {
                console.error("Error fetching attachment content:", error);
            }
        };

        fetchAttachmentContent();

        return () => {
            setContent(undefined);
        };
    }, [
        attachmentsIndex,
        featureId,
        layerId,
        attachmentsLayer.AttachmentTableID,
        attachments,
    ]);

    return (
        <>
            {!canCreateAttachment && !canDeleteAttachment ? (
                <div>
                    <div>No Attachments</div>
                    <Button onClick={handleAddAttachment}>Add Attachment</Button>
                </div>
            ) : (
                <>
                    {canCreateAttachment && (
                        <div onClick={onClick} className="attachment-link">
                            View/Add Attachments
                        </div>
                    )}

                    {show && (
                        <Dialog title={"Attachments"} onClose={onClick} width={500}>
                            {dialogContext === "addAttachment" && (
                                <AddAttachment
                                    attachments={attachments}
                                    setNewAttachments={setAttachments}
                                    setDialogContext={setDialogContext}
                                    layerID={layerId}
                                    objectID={featureId}
                                    attachmentId={attachmentsLayer.AttachmentTableID}
                                    attachmentOID={
                                        attachments &&
                                        attachmentsIndex >= 0 &&
                                        attachmentsIndex < attachments.length &&
                                        attachments[attachmentsIndex] &&
                                        attachments[attachmentsIndex].id
                                    }
                                />
                            )}

                            {dialogContext === "deleteAttachment" &&
                                attachments[attachmentsIndex].id && (
                                    <DeleteAttachment
                                        attachments={attachments}
                                        setNewAttachments={setAttachments}
                                        setDialogContext={setDialogContext}
                                        layerID={layerId}
                                        objectID={featureId}
                                        attachmentId={attachmentsLayer.AttachmentTableID}
                                        attachmentOID={
                                            attachments &&
                                            attachmentsIndex >= 0 &&
                                            attachmentsIndex < attachments.length &&
                                            attachments[attachmentsIndex] &&
                                            attachments[attachmentsIndex].id
                                        }
                                        parentHandleDelete={handleDeleteAttachment}
                                    />
                                )}
                            <div
                                style={
                                    dialogContext !== "viewAttachment"
                                        ? { display: "none" }
                                        : null
                                }>
                                <div>
                                    {attachments[attachmentsIndex] &&
                                        attachments[attachmentsIndex].name}
                                </div>
                                <div>
                                    {attachments.length === 0
                                        ? "No attachments"
                                        : `${attachmentsIndex + 1}/${attachments.length}`}
                                </div>
                                {content && content.type === "image" && (
                                    <>
                                        {" "}
                                        <img
                                            src={content.src}
                                            alt="attachment"
                                            style={{ width: "100%" }}
                                        />
                                        <div
                                            className="attachment-link"
                                            onClick={() =>
                                                downloadAttachment(
                                                    content.src,
                                                    attachments[attachmentsIndex].name
                                                )
                                            }>
                                            Download image
                                        </div>{" "}
                                    </>
                                )}
                                {content && content.type === "link" && (
                                    <div
                                        className="attachment-link"
                                        onClick={() =>
                                            downloadAttachment(
                                                content.src,
                                                attachments[attachmentsIndex].name
                                            )
                                        }>
                                        Download file
                                    </div>
                                )}
                                <div className="attachment-buttons">
                                    <Button
                                        icon="arrow-left"
                                        onClick={handleLeftArrowClick}
                                    />
                                    <div className="attachment-actions">
                                        {canCreateAttachment && (
                                            <Button
                                                style={{ margin: "0 5px" }}
                                                onClick={handleAddAttachment}>
                                                {" "}
                                                Add{" "}
                                            </Button>
                                        )}
                                        {canDeleteAttachment && (
                                            <DeleteAttachment
                                                attachments={attachments}
                                                setNewAttachments={setAttachments}
                                                setDialogContext={setDialogContext}
                                                layerID={layerId}
                                                objectID={featureId}
                                                attachmentId={
                                                    AttachmentsConfig.Attachment
                                                        .AttachmentTableID
                                                }
                                                attachmentOID={
                                                    attachments &&
                                                    attachmentsIndex >= 0 &&
                                                    attachmentsIndex <
                                                        attachments.length &&
                                                    attachments[attachmentsIndex] &&
                                                    attachments[attachmentsIndex].id
                                                }
                                                parentHandleDelete={
                                                    handleDeleteAttachment
                                                }
                                                onClick={handleDeleteAttachment}
                                            />
                                        )}
                                    </div>
                                    <Button
                                        icon="arrow-right"
                                        onClick={handleRightArrowClick}
                                    />
                                </div>
                            </div>
                        </Dialog>
                    )}
                </>
            )}
        </>
    );
};
/**
 * Retrieves an attachment for a given layer and feature by OID.
 *
 * @param {string} layerId - The ID of the layer.
 * @param {string} featureId - The ID of the feature.
 * @param {number} attachmentid - The ID of the attachment.
 * @param {number} attachmentoid - The ID of the attachment object.
 * @return {string} The base64-encoded attachments.
 */
export const getAttachment = async (layerId, featureId, attachmentid, attachmentoid) => {
    let res = { data: [], headers: {} }; // Set a default value for res
    try {
        res = await dataAPI.get(
            `/Layers/${layerId}/Data/${featureId}/Attachments/${attachmentid}/${attachmentoid}`,
            {
                responseType: "arraybuffer",
            }
        );
    } catch (error) {
        console.log(error);
        return; // Return from the function to prevent the rest of the code from executing
    }
    const base64 = Buffer.from(new Uint8Array(res.data)).toString("base64");
    return `data:${res.headers["content-type"]};base64,${base64}`;
};

/**
 * Retrieves the attachments for a given layer and feature.
 *
 * @param {string} layerId - The ID of the layer.
 * @param {string} featureId - The ID of the feature.
 * @param {number} attachmentid - The ID of the attachment.
 * @return {string} The base64-encoded attachments.
 */
export const getFeatureAttachments = async (layerId, featureId, attachmentid) => {
    let res = { data: [], headers: {} }; // Set a default value for res
    try {
        res = await dataAPI.get(
            `/Layers/${layerId}/Data/${featureId}/Attachments/${attachmentid}`
        );
    } catch (error) {
        console.log(error);
        return; // Return from the function to prevent the rest of the code from executing
    }

    return res.data;
};

/**
 *
 * @param {AttachmentsConfig} attachments
 * @param {Array<string>} tokenRoles
 * @param {string} permissionValue
 * @returns {boolean} Indicates if user has permission to create an attachment
 */
const checkPermission = (attachments, tokenRoles, permissionValue) => {
    if (!attachments?.Permissions?.Permission) {
        return false;
    } else {
        /** @type {Permissions} */
        const matchedPermissions = makeArray(attachments.Permissions.Permission).filter(
            (p) => {
                return tokenRoles.includes(p.Value);
            }
        );
        /** @type {boolean} */
        const isRestricted = matchedPermissions.some((p) => {
            return p[permissionValue] === false || p[permissionValue] === undefined;
        });
        return isRestricted ? false : true;
    }
};

const getTokenRoles = () => {
    const token = getCookie("ast");

    let userGivenName = "Guest";
    let tokenRoles = ["GUEST"];
    if (token) {
        userGivenName = jwt_decode(token).preferred_username;
        tokenRoles = jwt_decode(token).realm_access.roles;
    }
    return tokenRoles;
};

export default React.memo(Attachments);
